import {
  CLASS_IRIS,
  OWL_DATATYPE_PROPERTY_IRI,
  OWL_OBJECT_PROPERTY_IRI,
  RDFS_DOMAIN_IRI,
  RDFS_LABEL_IRI,
  RDFS_RANGE_IRI,
  RULE_SPARQL_RULE_IRI,
  SCHEMA_ORG_DOMAIN_INCLUDES_IRI,
  SCHEMA_ORG_HTTP_DOMAIN_INCLUDES_IRI,
  SCHEMA_ORG_HTTP_RANGE_INCLUDES_IRI,
  SCHEMA_ORG_RANGE_INCLUDES_IRI,
  SUPPORTED_MODEL_PREDICATE_IRIS,
} from 'src/templates/queries/iris';

/**
 * Checks if there are triples that are not supported by Designer in a model.
 *
 * Used to determine whether to further query for unsupported triples
 *
 * **Call Frequency**
 *
 * Called once when a model is imported or uploaded through file
 *
 * **Context**
 * - Named graphs: set by the user (default is `reasoning.schema.graphs` value,
 *   which by default is `tag:stardog:api:context:local`)
 * - Reasoning: always off
 *
 * **Example**
 * ```sparql
# Has non model parts
ASK {
  VALUES (?predicate) {
    (<http://www.w3.org/2000/01/rdf-schema#comment>) (<http://www.w3.org/2004/02/skos/core#definition>) (<http://www.w3.org/2000/01/rdf-schema#label>) (<http://www.w3.org/2004/02/skos/core#prefLabel>) (<http://www.opengis.net/ont/geosparql#asWKT>) (<http://www.w3.org/2003/01/geo/wgs84_pos#lat>) (<http://www.w3.org/2003/01/geo/wgs84_pos#long>) (<tag:stardog:map:v1:node_size>) (<tag:stardog:map:v1:popup_title>) (<tag:stardog:map:v1:popup_content>) (<tag:stardog:map:v1:tooltip_title>) (<tag:stardog:map:v1:tooltip_content>) (<http://www.w3.org/2000/01/rdf-schema#domain>) (<https://schema.org/domainIncludes>) (<http://schema.org/domainIncludes>) (<http://www.w3.org/2000/01/rdf-schema#range>) (<https://schema.org/rangeIncludes>) (<http://schema.org/rangeIncludes>) (<http://www.w3.org/2000/01/rdf-schema#subClassOf>) (<https://schema.org/supersededBy>) (<http://schema.org/supersededBy>) (<http://www.w3.org/2000/01/rdf-schema#subPropertyOf>) (<http://www.w3.org/1999/02/22-rdf-syntax-ns#type>) (<tag:stardog:api:rule:content>)
  }
  ?subject ?predicate ?object
  FILTER NOT EXISTS {
    {
      VALUES (?innerObject) { (<http://www.w3.org/2002/07/owl#Class>) (<http://www.w3.org/2000/01/rdf-schema#Class>) (<https://schema.org/Class>) (<http://schema.org/Class>) (<http://www.w3.org/2004/02/skos/core#Concept>) (<tag:stardog:api:rule:SPARQLRule>) (<http://www.w3.org/2002/07/owl#DatatypeProperty>) (<http://www.w3.org/2002/07/owl#ObjectProperty>) }
      ?subject a ?innerObject
    } UNION {
      ?subject <http://www.w3.org/2000/01/rdf-schema#label> ?domain
    } UNION {
      ?subject <http://www.w3.org/2000/01/rdf-schema#domain> ?domain
    } UNION {
      ?subject <http://www.w3.org/2000/01/rdf-schema#range> ?range
    } UNION {
      ?subject <https://schema.org/domainIncludes>
              | <http://schema.org/domainIncludes>
              ?domainIncludes
    } UNION {
      ?subject <https://schema.org/rangeIncludes>
              | <http://schema.org/rangeIncludes>
              ?rangeIncludes
    }
  }
}
 * ```
 */
export const hasNonModelPartsQuery = (
  otherIris: string[]
) => `# Has non model parts
ASK {
  VALUES (?predicate) {
    ${[...SUPPORTED_MODEL_PREDICATE_IRIS, ...otherIris]
      .map((iri) => `(<${iri}>)`)
      .join(' ')}
  }
  ?subject ?predicate ?object
  FILTER NOT EXISTS {
    {
      VALUES (?innerObject) { ${[
        ...CLASS_IRIS,
        RULE_SPARQL_RULE_IRI,
        OWL_DATATYPE_PROPERTY_IRI,
        OWL_OBJECT_PROPERTY_IRI,
      ]
        .map((iri) => `(<${iri}>)`)
        .join(' ')} }
      ?subject a ?innerObject
    } UNION {
      ?subject <${RDFS_LABEL_IRI}> ?domain
    } UNION {
      ?subject <${RDFS_DOMAIN_IRI}> ?domain
    } UNION {
      ?subject <${RDFS_RANGE_IRI}> ?range
    } UNION {
      ?subject <${SCHEMA_ORG_DOMAIN_INCLUDES_IRI}>
              | <${SCHEMA_ORG_HTTP_DOMAIN_INCLUDES_IRI}>
              ?domainIncludes
    } UNION {
      ?subject <${SCHEMA_ORG_RANGE_INCLUDES_IRI}>
              | <${SCHEMA_ORG_HTTP_RANGE_INCLUDES_IRI}>
              ?rangeIncludes
    }${
      !otherIris.length
        ? ''
        : ` UNION {
      ?subject ${otherIris.map((iri) => `<${iri}>`).join(' | ')} ?object
    }`
    }
  }
}`;
